import { config, isPregnancyNode, TWIN_TYPE } from './config.js';
import { zygosityComponents } from './twinLinkage.js';

export function getRowDepthGroupOfPerson(person, rowDepthGroups) {
  return (
    rowDepthGroups.find((group) => group.some((p) => p.pid === person.pid)) ||
    null
  );
}
const _pidOrderCache = new WeakMap();

function getOrderTieBreak(person) {
  if (!person.family || !person.family.persons) return 0;
  const persons = person.family.persons;
  let cache = _pidOrderCache.get(persons);
  if (!cache) {
    const sorted = persons
      .map((p) => p.pid)
      .sort((a, b) => {
        const an = Number(a);
        const bn = Number(b);
        if (Number.isFinite(an) && Number.isFinite(bn)) return an - bn;
        return String(a).localeCompare(String(b));
      });
    cache = {
      map: new Map(sorted.map((pid, i) => [pid, i])),
      N: sorted.length
    };
    _pidOrderCache.set(persons, cache);
  }
  const { map, N } = cache;
  if (N <= 1) return 0;
  const idx = map.get(person.pid) ?? -1;
  return idx < 0 ? 0 : idx / N;
}

// --- Birth-order sort key ---------------------------------------------------
// Order siblings oldest → youngest, left → right (clinical convention). Tiered
// fallback so partial data degrades gracefully and the result stays
// deterministic (see claude-MDs/PLAN-sibling-birth-order.md):
//   tier 0 — known DOB:        sort by date ascending  (earliest = oldest = left)
//   tier 1 — no DOB but age:   sort by age descending  (oldest = left)
//   tier 2 — neither:          fall back to PID (entry order), deterministic last
// `dob` is stored as a full ISO YYYY-MM-DD string (partialDateToBackend pads a
// missing month/day to 01), so Date.parse suffices; `estimatedDob`/`dobPrecision`
// flag uncertainty but never disqualify a date. Gated by ENABLE_BIRTH_ORDER_SORT:
// when off, every node is tier 2 → pure PID order (the legacy behaviour).
function parseDobMs(dob) {
  if (!dob || typeof dob !== 'string') return NaN;
  const t = Date.parse(dob);
  return Number.isFinite(t) ? t : NaN;
}

export function siblingBirthKey(person) {
  const data = (person && person.data) || {};
  if (!config.ENABLE_BIRTH_ORDER_SORT) return [2, 0, person.pid];
  const ms = parseDobMs(data.dob);
  if (!Number.isNaN(ms)) return [0, ms, person.pid];
  const age = data.age != null && data.age !== '' ? Number(data.age) : NaN;
  if (!Number.isNaN(age)) return [1, -age, person.pid];
  // A pregnancy with no birth date/age is the youngest in its sibship (Bennett: the
  // current pregnancy sorts last / right-most). New tier 3 sits below the neither-DOB-
  // nor-age tier 2. Gated on ENABLE_REPRO_NODES so non-repro / flag-off ordering is
  // byte-identical (every existing sibling stays tier ≤2).
  if (config.ENABLE_REPRO_NODES && isPregnancyNode(person)) {
    return [3, 0, person.pid];
  }
  return [2, 0, person.pid];
}

export function compareBirthKey(a, b) {
  if (a[0] !== b[0]) return a[0] - b[0];
  if (a[1] !== b[1]) return a[1] - b[1];
  const an = Number(a[2]);
  const bn = Number(b[2]);
  if (Number.isFinite(an) && Number.isFinite(bn)) return an - bn;
  return String(a[2]).localeCompare(String(b[2]));
}

// Order the members WITHIN one twin set (TGCLOUD-1692).
//
// The renderer draws a monozygotic bar, and an unknown-zygosity "?", across a RUN of
// ADJACENT members only — so which subsets end up next to each other decides what the
// chart is able to say. Two nested levels, because the two glyphs do not compete
// equally:
//
//   OUTER  blocks over BOTH glyph-drawing zygosities (monozygotic and unknown), merged
//          where they overlap. Keeps an unknown component together whenever nothing
//          stronger objects — which is the whole set, in the common case of a set with
//          no recorded zygosity at all.
//   INNER  monozygotic components inside a block. These are subsets of the block by
//          construction, so making them contiguous cannot break the block.
//
// Dizygotic draws nothing, so it constrains nothing.
//
// Everything else is ranked by its own birth key, so birth order still governs wherever
// it does not fight a glyph, and a set with no monozygotic or unknown pair in it comes
// out in exactly the order it always did.
//
// KNOWN LIMIT, deliberate: only the monozygotic level is GUARANTEED. Requiring several
// overlapping subsets each to be consecutive is not always satisfiable, and where the
// two levels conflict — an unknown component straddling part of a monozygotic one —
// monozygotic wins, because a missing bar loses a genetic fact where a missing "?" loses
// only "we did not record it". Both are omissions rather than false claims, which is the
// property `zygosityRuns` exists to guarantee.
function orderWithinTwinSet(members) {
  const byBirth = (a, b) =>
    compareBirthKey(siblingBirthKey(a), siblingBirthKey(b));
  if (members.length < 3) return members.sort(byBirth);

  const identical = zygosityComponents(members, TWIN_TYPE.monozygotic);
  const unknown = zygosityComponents(members, TWIN_TYPE.unknown);
  if (!identical.length && !unknown.length) return members.sort(byBirth);

  // Outer blocks: union-find over every member that shares a glyph-drawing relation
  // with another, whichever of the two it is.
  const parent = new Map();
  const find = (x) => {
    if (!parent.has(x)) parent.set(x, x);
    if (parent.get(x) !== x) parent.set(x, find(parent.get(x)));
    return parent.get(x);
  };
  const union = (a, b) => parent.set(find(a), find(b));
  [...identical, ...unknown].forEach((component) => {
    component
      .slice(1)
      .forEach((pid) => union(String(component[0]), String(pid)));
  });

  const identicalOf = new Map();
  identical.forEach((component, index) => {
    component.forEach((pid) => identicalOf.set(String(pid), index));
  });

  const blocks = new Map();
  for (const p of members) {
    const key = String(p.pid);
    const root = parent.has(key) ? find(key) : `solo:${key}`;
    if (!blocks.has(root)) blocks.set(root, []);
    blocks.get(root).push(p);
  }

  const earliest = (people) =>
    people.map(siblingBirthKey).sort(compareBirthKey)[0];

  // Inside a block, identical components are the sub-blocks.
  const orderBlock = (people) => {
    if (people.length < 3) return people.sort(byBirth);
    const subBlocks = new Map();
    const solo = [];
    for (const p of people) {
      const index = identicalOf.get(String(p.pid));
      if (index == null) solo.push([p]);
      else {
        if (!subBlocks.has(index)) subBlocks.set(index, []);
        subBlocks.get(index).push(p);
      }
    }
    return [...subBlocks.values(), ...solo]
      .sort((a, b) => compareBirthKey(earliest(a), earliest(b)))
      .flatMap((sub) => sub.sort(byBirth));
  };

  return [...blocks.values()]
    .sort((a, b) => compareBirthKey(earliest(a), earliest(b)))
    .flatMap(orderBlock);
}

// Order a set of sibling persons left → right by birth order (DOB → age → PID),
// keeping twins (same twinSubGroupId) as ONE adjacent block — a singleton whose
// date falls between two twins must NOT slip between them. Returns the persons
// in order. Both layout engines call this so V1 and V2 can never drift.
//
// Within a twin set of three or more, `orderWithinTwinSet` additionally keeps each
// monozygotic component together, because the bar the renderer draws spans adjacent
// members only (TGCLOUD-1692).
export function orderSiblings(persons) {
  const groups = new Map();
  for (const p of persons) {
    const key = p.twinSubGroupId ? `t:${p.twinSubGroupId}` : `p:${p.pid}`;
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(p);
  }
  const groupKey = (g) => g.map(siblingBirthKey).sort(compareBirthKey)[0];
  const ordered = [...groups.values()].sort((a, b) =>
    compareBirthKey(groupKey(a), groupKey(b))
  );
  return ordered.flatMap((g) =>
    g.length > 2
      ? orderWithinTwinSet(g)
      : g.sort((a, b) =>
          compareBirthKey(siblingBirthKey(a), siblingBirthKey(b))
        )
  );
}

function getPersonSortKey(
  person,
  currentGen,
  genMap,
  allowSpouseBranch = true,
  visited = new Set()
) {
  if (visited.has(person.pid)) {
    return getOrderTieBreak(person) * 0.01;
  }
  visited.add(person.pid);
  const isDynamicallyAdded = person.isDynamicallyAdded === true;

  if (
    isDynamicallyAdded &&
    person.siblings.length > 0 &&
    !person.father &&
    !person.mother
  ) {
    const originalSiblings = person.siblings.filter(
      (s) => !s.isDynamicallyAdded
    );

    if (originalSiblings.length > 0) {
      const siblingWithChildren = originalSiblings.find(
        (s) => s.children.length > 0
      );
      if (siblingWithChildren) {
        const sibKey = getPersonSortKey(
          siblingWithChildren,
          currentGen,
          genMap,
          true,
          visited
        );
        return sibKey + getOrderTieBreak(person) * 0.01;
      }

      const siblingKeys = originalSiblings.map((s) =>
        getPersonSortKey(s, currentGen, genMap, true, visited)
      );
      const avgSiblingKey =
        siblingKeys.reduce((a, b) => a + b, 0) / siblingKeys.length;

      return avgSiblingKey + getOrderTieBreak(person) * 0.01;
    }

    return (person.ancestorScore() || 0) + getOrderTieBreak(person) * 0.01;
  }

  if (person.father || person.mother || person.siblings.length > 0) {
    const currentGenNodes = genMap.get(currentGen);
    if (currentGenNodes) {
      const halfSibSet = new Set();
      [person.father, person.mother].filter(Boolean).forEach((parent) => {
        parent.children.forEach((c) => {
          if (c.pid !== person.pid && currentGenNodes.includes(c)) {
            halfSibSet.add(c);
          }
        });
      });
      person.siblings.forEach((s) => {
        if (currentGenNodes.includes(s)) {
          halfSibSet.add(s);
        }
      });

      if (halfSibSet.size > 0) {
        const childGen = genMap.get(currentGen + 1);
        const allSibsInGroup = [person, ...Array.from(halfSibSet)];
        const siblingsWithDirectChildren = allSibsInGroup.filter(
          (s) => s.children.length > 0
        );

        let commonBase;
        if (childGen && siblingsWithDirectChildren.length > 0) {
          const anchors = siblingsWithDirectChildren
            .map((s) => {
              const childrenInNextGen = s.children.filter((c) =>
                childGen.includes(c)
              );
              if (childrenInNextGen.length === 0) return null;
              const indices = childrenInNextGen
                .map((c) => childGen.indexOf(c))
                .filter((i) => i >= 0);
              return indices.length > 0 ? Math.min(...indices) : null;
            })
            .filter((a) => a !== null);
          commonBase =
            anchors.length > 0
              ? anchors.reduce((a, b) => a + b, 0) / anchors.length
              : person.ancestorScore() || 0;
        } else {
          const stableHalfSibs = Array.from(halfSibSet).filter(
            (s) =>
              s.children.length > 0 ||
              s.spouses.some((sp) => sp.children.length > 0)
          );
          commonBase =
            stableHalfSibs.length > 0
              ? stableHalfSibs
                  .map((s) =>
                    getPersonSortKey(s, currentGen, genMap, true, visited)
                  )
                  .reduce((a, b) => a + b, 0) / stableHalfSibs.length
              : person.ancestorScore() || 0;
        }

        // Rank within the sibling group by BIRTH ORDER (DOB → age → PID), via the
        // SHARED orderSiblings helper — so twins stay an adjacent block and V1
        // produces the same left→right order as V2 (a flag flip won't reshuffle).
        const sibsInGroup = orderSiblings([person, ...Array.from(halfSibSet)]);
        const rank = sibsInGroup.findIndex((s) => s.pid === person.pid);
        return commonBase + (rank + 1) / (sibsInGroup.length + 1);
      }
    }
  }

  if (
    allowSpouseBranch &&
    person.spouses.length > 0 &&
    person.siblings.length === 0
  ) {
    const spouse = person.spouses[0];
    const spouseHasIndependentKey =
      spouse.siblings.length > 0 || spouse.children.length > 0;
    if (spouseHasIndependentKey) {
      const spouseSortKey = getPersonSortKey(
        spouse,
        currentGen,
        genMap,
        false,
        visited
      );

      // When the spouse has multiple partners, alternate sides:
      // first spouse goes left, second spouse goes right, etc.
      if (spouse.spouseIds.length > 1) {
        const spouseIndex = spouse.spouseIds.indexOf(person.pid);
        const offset = spouseIndex === 0 ? -0.001 : 0.001 * (spouseIndex + 1);
        return spouseSortKey + offset;
      }

      const offset = person.gender === 'male' ? -0.001 : 0.001;
      return spouseSortKey + offset;
    }
  }

  if (person.children.length > 0) {
    const childGen = genMap.get(currentGen + 1);
    if (childGen) {
      // Find children in the next generation
      const childrenInNextGen = person.children.filter((child) =>
        childGen.includes(child)
      );

      if (childrenInNextGen.length > 0) {
        // Get leftmost child's index
        const childIndices = childrenInNextGen.map((child) => {
          const idx = childGen.indexOf(child);
          return idx >= 0 ? idx : -1;
        });
        return Math.min(...childIndices);
      }
    }
  }

  // Spouse-children fallback: runs before parent-anchor for the same reason —
  // a node whose spouse has children should keep its child-derived branch key.
  if (person.spouses.length > 0) {
    const childGen = genMap.get(currentGen + 1);
    if (childGen) {
      for (const spouse of person.spouses) {
        if (spouse.children.length > 0) {
          const childrenInNextGen = spouse.children.filter((child) =>
            childGen.includes(child)
          );

          if (childrenInNextGen.length > 0) {
            const childIndices = childrenInNextGen.map((child) => {
              const idx = childGen.indexOf(child);
              return idx >= 0 ? idx : -1;
            });
            return Math.min(...childIndices);
          }
        }
      }
    }
  }
  if (person.father || person.mother || person.siblings.length > 0) {
    const currentGenNodes = genMap.get(currentGen);
    if (currentGenNodes) {
      const halfSibSet = new Set();
      [person.father, person.mother].filter(Boolean).forEach((parent) => {
        parent.children.forEach((c) => {
          if (c.pid !== person.pid && currentGenNodes.includes(c)) {
            halfSibSet.add(c);
          }
        });
      });
      person.siblings.forEach((s) => {
        if (currentGenNodes.includes(s)) {
          halfSibSet.add(s);
        }
      });
      if (halfSibSet.size > 0) {
        const stableHalfSibs = Array.from(halfSibSet).filter(
          (s) =>
            s.children.length > 0 ||
            s.spouses.some((sp) => sp.children.length > 0)
        );

        const base =
          stableHalfSibs.length > 0
            ? (() => {
                const ks = stableHalfSibs.map((s) =>
                  getPersonSortKey(s, currentGen, genMap, true, visited)
                );
                return ks.reduce((a, b) => a + b, 0) / ks.length;
              })()
            : person.ancestorScore() || 0;

        const sibPids = [
          person.pid,
          ...Array.from(halfSibSet).map((s) => s.pid)
        ].sort((a, b) => {
          const an = Number(a);
          const bn = Number(b);
          if (Number.isFinite(an) && Number.isFinite(bn)) return an - bn;
          return String(a).localeCompare(String(b));
        });
        const rank = sibPids.findIndex((p) => p === person.pid);
        return base + (rank + 1) / (sibPids.length + 1);
      }
    }
  }

  if (person.children.length === 0 && person.siblings.length > 0) {
    const siblingsWithChildren = person.siblings.filter(
      (s) => s.children.length > 0
    );
    if (siblingsWithChildren.length > 0) {
      const sibKeys = siblingsWithChildren.map((s) =>
        getPersonSortKey(s, currentGen, genMap, true, visited)
      );
      const avgSibKey = sibKeys.reduce((a, b) => a + b, 0) / sibKeys.length;

      return avgSibKey + getOrderTieBreak(person) * 0.0001;
    }
  }

  const score = person.ancestorScore();
  if (score === 0) {
    return getOrderTieBreak(person) * 0.01;
  }

  return score + getOrderTieBreak(person) * 0.01;
}

/**
 * After sorting a generation, ensure that when a person has multiple spouses,
 * the spouses are placed on opposite sides of that person.
 * The first spouse (by spouseIds order) stays on whichever side it landed;
 * subsequent spouses are moved to the other side.
 */
function ensureSpousesOnOppositeSides(sortedGen) {
  const processed = new Set();

  // We may need multiple passes since fixing one person can affect another
  let changed = true;
  let safetyCount = 0;
  while (changed && safetyCount < 10) {
    changed = false;
    safetyCount++;

    for (let i = 0; i < sortedGen.length; i++) {
      const person = sortedGen[i];
      if (processed.has(person.pid)) continue;
      if (!person.spouseIds || person.spouseIds.length < 2) continue;

      // Find which of this person's spouses are in this generation
      const spousesInGen = person.spouseIds
        .map((sid) => {
          const idx = sortedGen.findIndex((p) => p.pid === sid);
          return idx >= 0 ? { person: sortedGen[idx], idx, pid: sid } : null;
        })
        .filter(Boolean);

      if (spousesInGen.length < 2) continue;

      const personIdx = sortedGen.indexOf(person);
      if (personIdx < 0) continue;

      // Re-evaluate with current positions
      const onLeft = spousesInGen.filter((s) => s.idx < personIdx);
      const onRight = spousesInGen.filter((s) => s.idx > personIdx);

      if (onLeft.length > 0 && onRight.length > 0) {
        // Already on opposite sides
        processed.add(person.pid);
        continue;
      }

      // All spouses on same side — move the LAST spouse (by spouseIds order)
      // to the opposite side. The first spouse is the "primary" and stays.
      const firstSpousePid = person.spouseIds[0];
      const secondarySpouses = spousesInGen.filter(
        (s) => s.pid !== firstSpousePid
      );

      if (secondarySpouses.length === 0) {
        processed.add(person.pid);
        continue;
      }

      // Move the secondary spouse(s) to the opposite side
      const spouseToMove = secondarySpouses[0];
      const currentMoveIdx = sortedGen.indexOf(spouseToMove.person);

      if (currentMoveIdx < personIdx) {
        // Secondary spouse is on LEFT, first spouse is also on LEFT → move secondary to RIGHT
        const removed = sortedGen.splice(currentMoveIdx, 1)[0];
        const newPersonIdx = sortedGen.indexOf(person);
        sortedGen.splice(newPersonIdx + 1, 0, removed);
        changed = true;
      } else {
        // Secondary spouse is on RIGHT, first spouse is also on RIGHT → move secondary to LEFT
        const removed = sortedGen.splice(currentMoveIdx, 1)[0];
        const newPersonIdx = sortedGen.indexOf(person);
        sortedGen.splice(newPersonIdx, 0, removed);
        changed = true;
      }

      processed.add(person.pid);
      break; // Restart the loop after mutation
    }
  }
}

export function buildRowDepthGroups(family) {
  // Build a map first to store generations by their rowDepth number
  const genMap = new Map();

  // First pass: group people by generation (no sorting yet)
  for (let gen = family.minRowDepth; gen <= family.maxRowDepth; gen++) {
    const peopleInGen = family.persons.filter((p) => p.rowDepth === gen);
    genMap.set(gen, peopleInGen);
  }

  // Second pass: sort each generation from BOTTOM-UP (children first).
  for (let gen = family.maxRowDepth; gen >= family.minRowDepth; gen--) {
    const peopleInGen = genMap.get(gen);

    peopleInGen.sort((a, b) => {
      if (a.componentId !== b.componentId) {
        const aOrder = family.componentSortOrder.get(a.componentId) ?? 0;
        const bOrder = family.componentSortOrder.get(b.componentId) ?? 0;

        if (aOrder !== bOrder) {
          return aOrder - bOrder;
        }
      }

      const aKey = getPersonSortKey(a, gen, genMap);
      const bKey = getPersonSortKey(b, gen, genMap);
      return aKey - bKey;
    });

    // Post-sort fix: ensure multi-spouse nodes have spouses on opposite sides.
    // If a person has 2+ spouses and they ended up on the same side after
    // sorting, move the second spouse to the other side.
    ensureSpousesOnOppositeSides(peopleInGen);
  }

  for (let gen = family.minRowDepth; gen < family.maxRowDepth; gen++) {
    const parentGen = genMap.get(gen);
    const childGen = genMap.get(gen + 1);
    if (!parentGen || !childGen) continue;

    // Build an ordered list of parent-pair groups.
    // For each person in the parent generation (left-to-right), iterate their
    // spouses in left-to-right order so that children of each successive
    // spouse pair appear in the correct horizontal sequence.
    const parentPairGroups = [];
    const processedPairs = new Set();

    for (const parent of parentGen) {
      if (parent.children.length === 0) continue;

      // Collect spouse pairings for this parent, ordered by spouse position.
      // Include a null spouse entry for single-parent children.
      const spouseEntries = [];

      // Children with a specific spouse
      const spousesInGen = (parent.spouseIds || [])
        .map((sid) => parentGen.find((p) => p.pid === sid))
        .filter(Boolean);

      // Sort spouses by their position in parentGen (left to right)
      spousesInGen.sort((a, b) => parentGen.indexOf(a) - parentGen.indexOf(b));

      for (const spouse of spousesInGen) {
        spouseEntries.push(spouse);
      }

      // Also handle single-parent children (no matching spouse in parentGen)
      const hasOrphanChildren = parent.children.some((c) => {
        if (!childGen.includes(c)) return false;
        const otherParentId =
          c.motherId === parent.pid ? c.fatherId : c.motherId;
        return (
          !otherParentId || !parentGen.find((p) => p.pid === otherParentId)
        );
      });
      if (hasOrphanChildren) {
        spouseEntries.push(null); // null = single parent children
      }

      for (const spouse of spouseEntries) {
        const pairKey = spouse
          ? [parent.pid, spouse.pid].sort().join('|')
          : `single-${parent.pid}`;

        if (processedPairs.has(pairKey)) continue;
        processedPairs.add(pairKey);

        // Collect children of this parent pair in childGen
        const pairChildren = childGen.filter((c) => {
          const cm = c.motherId ?? '__none__';
          const cf = c.fatherId ?? '__none__';
          if (spouse) {
            const ck = [cm, cf].sort().join('|');
            const expectedKey = [parent.pid, spouse.pid].sort().join('|');
            return ck === expectedKey;
          } else {
            // Single parent: child has this parent but no other parent in parentGen
            const hasThisParent = cm === parent.pid || cf === parent.pid;
            if (!hasThisParent) return false;
            const otherPid = cm === parent.pid ? cf : cm;
            return (
              otherPid === '__none__' ||
              !parentGen.find((p) => p.pid === otherPid)
            );
          }
        });

        if (pairChildren.length === 0) continue;

        // Preserve the relative order from the bottom-up sort within each group
        pairChildren.sort((a, b) => childGen.indexOf(a) - childGen.indexOf(b));

        parentPairGroups.push(pairChildren);
      }
    }

    const spouseBridgePids = new Set();
    for (let gi = 0; gi < parentPairGroups.length; gi++) {
      for (const child of parentPairGroups[gi]) {
        if (!child.spouseIds || child.spouseIds.length === 0) continue;
        for (let gj = 0; gj < parentPairGroups.length; gj++) {
          if (gi === gj) continue;
          const partnerInOtherGroup = parentPairGroups[gj].find((c) =>
            child.spouseIds.includes(c.pid)
          );
          if (partnerInOtherGroup) {
            if (
              parentPairGroups[gj].length >= parentPairGroups[gi].length &&
              !spouseBridgePids.has(partnerInOtherGroup.pid)
            ) {
              spouseBridgePids.add(child.pid);
            }
            break;
          }
        }
      }
    }

    for (const group of parentPairGroups) {
      for (let i = group.length - 1; i >= 0; i--) {
        if (spouseBridgePids.has(group[i].pid)) {
          group.splice(i, 1);
        }
      }
    }

    const allPairedPids = new Set(parentPairGroups.flat().map((c) => c.pid));
    const placed = new Set();
    const newChildGen = [];

    // Helper: place a person and recursively place their unplaced spouses
    // that are in childGen but not part of any parent-pair group.
    const placeWithSpouseChain = (person, direction) => {
      if (placed.has(person.pid)) return;
      if (direction === 'before') {
        newChildGen.splice(newChildGen.length, 0, person);
      } else {
        newChildGen.push(person);
      }
      placed.add(person.pid);

      // Place this person's spouses that aren't already placed/paired
      const personOrigIdx = childGen.indexOf(person);
      const unplacedSpouses = (person.spouseIds || [])
        .map((sid) => childGen.find((p) => p.pid === sid))
        .filter((s) => s && !allPairedPids.has(s.pid) && !placed.has(s.pid));

      const spousesBefore = unplacedSpouses.filter(
        (s) => childGen.indexOf(s) < personOrigIdx
      );
      const spousesAfter = unplacedSpouses.filter(
        (s) => childGen.indexOf(s) >= personOrigIdx
      );

      // Insert "before" spouses right before the person we just added
      for (const sp of spousesBefore) {
        newChildGen.splice(newChildGen.indexOf(person), 0, sp);
        placed.add(sp.pid);
        // Recursively place the spouse's own spouses
        const spSpouses = (sp.spouseIds || [])
          .map((sid) => childGen.find((p) => p.pid === sid))
          .filter((s) => s && !allPairedPids.has(s.pid) && !placed.has(s.pid));
        for (const ss of spSpouses) {
          placeWithSpouseChain(ss, 'before');
        }
      }
      // Insert "after" spouses right after the person
      for (const sp of spousesAfter) {
        placeWithSpouseChain(sp, 'after');
      }
    };

    for (const group of parentPairGroups) {
      for (const child of group) {
        if (placed.has(child.pid)) continue;
        newChildGen.push(child);
        placed.add(child.pid);

        const childOrigIdx = childGen.indexOf(child);
        const spousesInGen = (child.spouseIds || [])
          .map((sid) => childGen.find((p) => p.pid === sid))
          .filter((s) => s && !allPairedPids.has(s.pid) && !placed.has(s.pid));

        // Sort spouses by their original position relative to child
        const spousesBefore = spousesInGen.filter(
          (s) => childGen.indexOf(s) < childOrigIdx
        );
        const spousesAfter = spousesInGen.filter(
          (s) => childGen.indexOf(s) >= childOrigIdx
        );

        // Insert "before" spouses (and their spouse chains) before the child
        for (const sp of spousesBefore) {
          newChildGen.splice(newChildGen.indexOf(child), 0, sp);
          placed.add(sp.pid);
          // Place spouse's own additional spouses
          const spSpouses = (sp.spouseIds || [])
            .map((sid) => childGen.find((p) => p.pid === sid))
            .filter(
              (s) => s && !allPairedPids.has(s.pid) && !placed.has(s.pid)
            );
          for (const ss of spSpouses) {
            placeWithSpouseChain(ss, 'before');
          }
        }
        // Insert "after" spouses (and their spouse chains) after the child
        for (const sp of spousesAfter) {
          placeWithSpouseChain(sp, 'after');
        }
      }
    }

    for (const c of childGen) {
      if (!placed.has(c.pid)) {
        newChildGen.push(c);
        placed.add(c.pid);
      }
    }

    if (newChildGen.length === childGen.length) {
      childGen.length = 0;
      childGen.push(...newChildGen);
    }

    const areSiblings = (a, b) => {
      if (!a.motherId && !a.fatherId) return false;
      if (!b.motherId && !b.fatherId) return false;
      return a.motherId === b.motherId && a.fatherId === b.fatherId;
    };

    const hasAnySiblingPairs = parentGen.some((a, i) =>
      parentGen.slice(i + 1).some((b) => areSiblings(a, b))
    );

    if (!hasAnySiblingPairs) {
      const originalParentOrder = parentGen.map((p) => p.pid);

      parentGen.sort((a, b) => {
        const aIdxs = a.children
          .map((c) => childGen.indexOf(c))
          .filter((i) => i >= 0);
        const bIdxs = b.children
          .map((c) => childGen.indexOf(c))
          .filter((i) => i >= 0);
        if (aIdxs.length === 0 && bIdxs.length === 0) {
          return (
            originalParentOrder.indexOf(a.pid) -
            originalParentOrder.indexOf(b.pid)
          );
        }
        if (aIdxs.length === 0) return 1;
        if (bIdxs.length === 0) return -1;
        const aCenter = aIdxs.reduce((s, i) => s + i, 0) / aIdxs.length;
        const bCenter = bIdxs.reduce((s, i) => s + i, 0) / bIdxs.length;
        return aCenter - bCenter;
      });
    }
  }

  // Convert map to array in correct order (gen 0, gen 1, gen 2, ...)
  const groups = [];
  for (let gen = family.minRowDepth; gen <= family.maxRowDepth; gen++) {
    groups.push(genMap.get(gen));
  }

  return groups;
}

export function logRowDepthGroups(rowDepthGroups, title = 'Row Depth Groups') {
  if (!config.DEBUG_MODE) return;

  console.log(`\n=== ${title} ===`);
  rowDepthGroups.forEach((group, index) => {
    const people = group
      .map(
        (p) =>
          `${p.pid}(${
            typeof p.virtualX === 'number' ? p.virtualX.toFixed(1) : '?'
          })`
      )
      .join(', ');
    console.log(`Gen ${index}: [${people}]`);
  });
}

export function normalizeXToStartAtZero(persons) {
  if (persons.length === 0) return;

  const minX = Math.min(...persons.map((p) => p.virtualX));

  if (minX !== 0) {
    persons.forEach((person) => {
      person.virtualX -= minX;
    });
  }
}
